/**
 * Authenticated group-key distribution — prevents forged membership / sender-key injection.
 * Canonical bytes are signed with the admin messaging Ed25519 key.
 *
 * Wire members intentionally omit hybridPublicKeyHex / contactId: ML-KEM hybrid
 * pubkeys (~2.4 KiB hex each) blow past the ratchet 8 KiB pad bucket (SPEC §9.5 /
 * NFR text ≤8 KiB) even at 4 members. Recipients hydrate hybrid keys from local
 * contacts (FR-GRP-001 pairwise fan-out assumes prior contact mesh or invite).
 */

import type { GroupRole } from "./groups";

/** Membership fields carried (and signed) on the group-key wire. */
export type GroupKeyMember = {
  identityKeyHex: string;
  displayName: string;
  role: GroupRole;
};

export type SignedGroupKeyPayload = {
  op: "group-key";
  groupId: string;
  epoch: number;
  title: string;
  members: GroupKeyMember[];
  senderKeyB64: string;
  /** Admin messaging public key (hex) that produced adminSigHex */
  adminIdentityKeyHex: string;
  /** Ed25519 signature over canonicalGroupKeyBytes (hex) */
  adminSigHex: string;
};

/** Strip local-only / oversized fields before seal + sign. */
export function toWireGroupMembers(
  members: { identityKeyHex: string; displayName: string; role: GroupRole }[],
): GroupKeyMember[] {
  return members.map((m) => ({
    identityKeyHex: m.identityKeyHex,
    displayName: m.displayName,
    role: m.role,
  }));
}

/** Stable canonical encoding for signatures (sorted member keys). */
export function canonicalGroupKeyBytes(p: {
  groupId: string;
  epoch: number;
  title: string;
  members: GroupKeyMember[];
  senderKeyB64: string;
  adminIdentityKeyHex: string;
}): Uint8Array {
  const members = toWireGroupMembers(p.members)
    .map((m) => ({
      identityKeyHex: m.identityKeyHex.toLowerCase(),
      displayName: m.displayName,
      role: m.role,
    }))
    .sort((a, b) => a.identityKeyHex.localeCompare(b.identityKeyHex));
  const obj = {
    v: 1,
    op: "group-key",
    groupId: p.groupId,
    epoch: p.epoch,
    title: p.title,
    members,
    senderKeyB64: p.senderKeyB64,
    adminIdentityKeyHex: p.adminIdentityKeyHex.toLowerCase(),
  };
  return new TextEncoder().encode(JSON.stringify(obj));
}

export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.replace(/\s+/g, "");
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Domain checks before cryptographic verify:
 * - admin must be listed with role admin
 * - epoch succession rules vs prior local state
 */
export function validateGroupKeyAuthorization(opts: {
  payload: SignedGroupKeyPayload;
  prior:
    | { epoch: number; members: { identityKeyHex: string; role: GroupRole }[] }
    | null
    | undefined;
}): { ok: true } | { ok: false; reason: string } {
  const { payload, prior } = opts;
  const admin = payload.members.find(
    (m) => m.identityKeyHex.toLowerCase() === payload.adminIdentityKeyHex.toLowerCase(),
  );
  if (!admin || admin.role !== "admin") {
    return { ok: false, reason: "signer is not an admin member of the proposed epoch" };
  }
  if (!prior) {
    // Create is normally epoch 1; late join after addMembers (FR-GRP-003) may first
    // observe epoch > 1. Still require a listed admin signer (checked above).
    if (payload.epoch < 1) {
      return { ok: false, reason: "invalid epoch" };
    }
    return { ok: true };
  }
  if (payload.epoch < prior.epoch) {
    return { ok: false, reason: "epoch rollback rejected" };
  }
  if (payload.epoch === prior.epoch) {
    // Re-fanout of same epoch is allowed only from a prior admin.
    const wasAdmin = prior.members.some(
      (m) =>
        m.identityKeyHex.toLowerCase() === payload.adminIdentityKeyHex.toLowerCase() &&
        m.role === "admin",
    );
    if (!wasAdmin) return { ok: false, reason: "same-epoch rekey requires prior admin signer" };
    return { ok: true };
  }
  // Advance or catch-up jump (epoch > prior+1). Removed members never receive the
  // removal epoch key (FR-GRP-002), so re-add (FR-GRP-003) arrives as a jump — still
  // require a prior-admin signature over the proposed membership + sender key.
  const wasAdmin = prior.members.some(
    (m) =>
      m.identityKeyHex.toLowerCase() === payload.adminIdentityKeyHex.toLowerCase() &&
      m.role === "admin",
  );
  if (!wasAdmin) {
    return { ok: false, reason: "epoch advance must be signed by a prior admin" };
  }
  return { ok: true };
}
